import { useCallback } from "react"

import { type UpcomingShift } from "@/hooks/useActiveEngagements"
import { supabase } from "@/lib/supabase"

import type { Enums } from "../../database.types"

type SheriffType = Enums<"person_type">

const fetchMismatchedUpcomingShifts = async (
  personId: string,
  sheriffType: SheriffType,
): Promise<UpcomingShift[]> => {
  const { data, error } = await supabase
    .from("shifts")
    .select("id, start_time, end_time, area, location")
    .eq("person_id", personId)
    .eq("status", "Filled")
    .gt("start_time", new Date().toISOString())
    .neq("type", sheriffType)
    .order("start_time", { ascending: true })
  if (error) throw error
  return (data ?? []) as UpcomingShift[]
}

const fetchCompletionShiftConflicts = async (applicationId: string): Promise<UpcomingShift[]> => {
  const { data: application, error: appError } = await supabase
    .from("applications")
    .select("person_id, job:job_id(sheriff_type)")
    .eq("id", applicationId)
    .single()
  if (appError) throw appError

  const job = Array.isArray(application?.job) ? application.job[0] : application?.job
  const toType = job?.sheriff_type
  const personId = application?.person_id
  if (!toType || !personId) return []

  return fetchMismatchedUpcomingShifts(personId, toType)
}

export const useFetchMismatchedUpcomingShifts = () =>
  useCallback(
    ({ personId, sheriffType }: { personId: string; sheriffType: SheriffType }) =>
      fetchMismatchedUpcomingShifts(personId, sheriffType),
    [],
  )

export const useFetchCompletionShiftConflicts = () =>
  useCallback((applicationId: string) => fetchCompletionShiftConflicts(applicationId), [])
